import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { ShoppingCart, ChevronLeft, ChevronRight, Package, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import { Button } from '../../components/ui/button';
import { Separator } from '../../components/ui/separator';
import { productService } from '../../services/productService';
import { useCart } from '../../store/cartContext';
import { Skeleton } from '../../components/ui/skeleton';
import { QuantityStepper } from '../../components/shared/QuantityStepper';
import { applyProductSeo } from '@/utils/seo';
import { DeliveryNotice } from '../../components/shared/DeliveryNotice';

function plainOneLine(text) {
  if (!text || typeof text !== 'string') return '';
  return text.replace(/\s+/g, ' ').trim().slice(0, 155);
}

export default function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addItem } = useCart();
  const previewProduct = location.state?.productPreview;
  const canUsePreview = previewProduct && String(previewProduct.id) === String(id);

  const [product, setProduct] = useState(canUsePreview ? previewProduct : null);
  const [loading, setLoading] = useState(!canUsePreview);
  const [quantity, setQuantity] = useState(1);
  const [activeImage, setActiveImage] = useState(0);
  const [added, setAdded] = useState(false);

  const allImages = product
    ? (product.images && product.images.length > 0 ? product.images : [product.primaryImageUrl].filter(Boolean))
    : [];

  useEffect(() => {
    setQuantity(1);
    setActiveImage(0);
    if (!canUsePreview) {
      setLoading(true);
      setProduct(null);
    }

    productService.getProductById(id)
      .then((data) => {
        setProduct(data);
        setLoading(false);
      })
      .catch(() => navigate('/proizvodi'));
  }, [id, canUsePreview, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!product) return;
    const snippet = plainOneLine(product.shortDescription || product.description || '');
    const description = snippet
      ? `${snippet} Domaći i sjenički proizvodi u Prijepolju — Fazlić.`
      : `${product.name}: domaći i sjenički proizvod u Prijepolju. Online poručivanje.`;
    applyProductSeo({
      title: `${product.name} | Sjenički proizvodi Prijepolje — Fazlić`,
      description,
      path: `/proizvodi/${id}`,
    });
  }, [product, id]);

  const handleAddToCart = () => {
    if (!product?.isAvailable) return;
    addItem(product, quantity);
    setAdded(true);
    setTimeout(() => setAdded(false), 2500);
  };

  const goToPrevImage = () => {
    if (allImages.length <= 1) return;
    setActiveImage((prev) => (prev === 0 ? allImages.length - 1 : prev - 1));
  };

  const goToNextImage = () => {
    if (allImages.length <= 1) return;
    setActiveImage((prev) => (prev === allImages.length - 1 ? 0 : prev + 1));
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <Skeleton className="aspect-square rounded-2xl" />
          <div className="space-y-4">
            <Skeleton className="h-8 w-3/4" />
            <Skeleton className="h-5 w-1/4" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm font-ui text-muted-foreground mb-8">
        <Link to="/" className="hover:text-foreground transition-colors">Početna</Link>
        <ChevronRight size={14} />
        <Link to="/proizvodi" className="hover:text-foreground transition-colors">Proizvodi</Link>
        <ChevronRight size={14} />
        <span className="text-foreground">{product.name}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 xl:gap-16">
        {/* Gallery */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="rounded-2xl overflow-hidden shadow-elevated mb-4 relative">
            <img
              src={allImages[activeImage] || product.primaryImageUrl}
              alt={product.name}
              className="w-full aspect-square object-cover"
            />
            {allImages.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={goToPrevImage}
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-background/80 hover:bg-background text-foreground border border-border flex items-center justify-center shadow-card transition-colors"
                  aria-label="Prethodna slika"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  type="button"
                  onClick={goToNextImage}
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-9 w-9 rounded-full bg-background/80 hover:bg-background text-foreground border border-border flex items-center justify-center shadow-card transition-colors"
                  aria-label="Sledeca slika"
                >
                  <ChevronRight size={18} />
                </button>
              </>
            )}
          </div>
          {allImages.length > 1 && (
            <div className="flex gap-3">
              {allImages.map((img, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImage(i)}
                  className={`rounded-xl overflow-hidden border-2 transition-all ${
                    activeImage === i ? 'border-primary shadow-card' : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt={`${product.name} ${i + 1}`} className="h-20 w-20 object-cover" />
                </button>
              ))}
            </div>
          )}
        </motion.div>

        {/* Product info */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="flex flex-col"
        >
          {/* Category */}
          <span className="badge-green mb-3 self-start">{product.categoryName}</span>

          {/* Title */}
          <h1 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-3 leading-tight">
            {product.name}
          </h1>

          {/* Price */}
          <div className="flex items-baseline gap-2 mb-5">
            <span className="font-ui text-3xl font-bold text-primary">
              {product.price.toLocaleString('sr-RS')} RSD
            </span>
            <span className="text-muted-foreground text-sm font-ui">/ {product.unit}</span>
          </div>

          {/* Short desc */}
          <p className="font-body-serif text-foreground/80 leading-relaxed text-base mb-5">
            {product.shortDescription}
          </p>

          <Separator className="mb-5" />

          <DeliveryNotice variant="compact" className="mb-5" />

          {/* Full desc */}
          <div className="prose prose-sm max-w-none mb-6">
            <p className="font-body-serif text-muted-foreground leading-relaxed">
              {product.description}
            </p>
          </div>

          {/* Availability */}
          <div className={`flex items-center gap-2 mb-6 text-sm font-ui font-medium ${
            product.isAvailable ? 'text-primary' : 'text-muted-foreground'
          }`}>
            {product.isAvailable ? (
              <><Check size={16} className="text-primary" /> Dostupno za poručivanje</>
            ) : (
              <><Package size={16} /> Trenutno nije dostupno</>
            )}
          </div>

          {product.isAvailable && (
            <>
              <div className="mb-6">
                <QuantityStepper
                  label={`Količina (${product.unit})`}
                  value={quantity}
                  onChange={setQuantity}
                  min={1}
                  max={99}
                />
              </div>

              {/* Add to cart */}
              <Button
                size="lg"
                onClick={handleAddToCart}
                className={`w-full font-ui font-semibold shadow-btn transition-all duration-300 ${
                  added
                    ? 'bg-primary-glow text-primary-foreground'
                    : 'bg-primary text-primary-foreground hover:bg-primary-light'
                }`}
              >
                {added ? (
                  <><Check size={18} className="mr-2" /> Dodato u korpu!</>
                ) : (
                  <><ShoppingCart size={18} className="mr-2" /> Dodaj u korpu - {(product.price * quantity).toLocaleString('sr-RS')} RSD</>
                )}
              </Button>
            </>
          )}

          {/* Tags */}
          <div className="mt-6 p-4 bg-muted/50 rounded-xl space-y-2 text-sm font-ui">
            <div className="flex gap-2">
              <span className="text-muted-foreground">Kategorija:</span>
              <span className="text-foreground font-medium">{product.categoryName}</span>
            </div>
            <div className="flex gap-2">
              <span className="text-muted-foreground">Jedinica:</span>
              <span className="text-foreground font-medium">{product.unit}</span>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
